"use client";

import { JSONContentRenderer } from "@/components/common/editors/json-renderer";
import ProfilePicture from "@/components/common/profile-picture";
import IncognitoVIPIcon from "@/icons/incognito-vip";
import { cn, iterateOverNodeFromJsonContent, deleteNodeFromJsonContent } from "@/lib/utils";
import { TCommunityFeedPost, TPostCustomContent, TPostLink, TPostMedia } from "@/types/post";
import { formatDistanceStrict } from "date-fns";
import {
	ClockIcon,
	EyeIcon,
	MessageCircleIcon,
	MoreHorizontal,
	PencilIcon,
	Pin,
} from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import DeletePostAlertDialog from "../../post/components/delete-post";
import FeedPostMediaCarousel from "./feed-post-carousel";
import FeedPostImagesGrid from "./feed-post-grid";
import FeedPostLikeButton from "./feed-post-like-button";
import FeedPostShareButton from "./feed-post-share-button";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getEditorConfig } from "@/components/common/editors/base-editor/base-editor";
import { POSTS_API } from "@/lib/apis";
import Youtube from "@tiptap/extension-youtube";
import CustomImage from "@/components/common/editors/extensions/custom-image";
import { SmartLink } from "@/components/common/editors/extensions/smart-link/smart-link";
import { useCommunityContext } from "@/providers/community-provider";

export type ParsedFeedPost = Omit<TCommunityFeedPost, "customContent"> & {
	customContent: TPostCustomContent;
};

const MediaPanel = ({ post }: { post: ParsedFeedPost }) => {
	const postImages: TPostMedia[] = post.media.filter((media) => !!media.objectPath) ?? [];
	const postLinks: TPostLink[] = post.customContent.links ?? [];

	if (post.content)
		iterateOverNodeFromJsonContent({
			jsonContent: post.content,
			func: (e) => {
				if (!e.attrs) return;
				switch (e.type) {
					case CustomImage.name: {
						const { src, alt } = e.attrs;
						postImages.push({
							objectPath: src,
							alt: alt,
						});
						break;
					}
					case Youtube.name: {
						const { src } = e.attrs;
						postLinks.push({
							url: src,
						});
						break;
					}
					case SmartLink.name: {
						const { href } = e.attrs;
						postLinks.push({
							url: href,
						});
						break;
					}
				}
			},
		});

	return (
		<>
			{postImages.length + postLinks.length > 0 &&
				(post.customContent.imageViewType === "carousel" ? (
					<FeedPostMediaCarousel links={postLinks} images={postImages} />
				) : post.customContent.imageViewType === "grid" ? (
					<FeedPostImagesGrid items={postImages} />
				) : null)}
		</>
	);
};

const FullPostContent = ({ post }: { post: ParsedFeedPost }) => {
	const renderingExtentions = getEditorConfig({
		type: post.postType === "ARTICLE" ? "article" : "discussion",
	}).extensions;
	return (
		<>
			<h5 className="mb-3 font-semibold leading-tight tracking-tight text-gray-800 md:text-lg md:leading-normal">
				{post.title}
			</h5>
			<JSONContentRenderer
				className="flex flex-col gap-4"
				json={post.content || {}}
				extensions={renderingExtentions}
			/>
		</>
	);
};

const PostContentPreview = ({ post, onClick }: { post: ParsedFeedPost; onClick: () => void }) => {
	const [showReadMore, setShowReadMore] = useState(false);
	const divRef = useRef<HTMLDivElement | null>(null);
	const maxDivHeightinPixels = 128;
	// TODO (zeekhuge): When changing the content height is less than max-height, the UI essentially
	// changes to full-post-view. See if we can in fact have 1 component for both.

	const renderingExtentions = [
		...getEditorConfig({
			type: post.postType === "ARTICLE" ? "article" : "discussion",
		}).extensions,
	];

	let content = post.content;
	if (content)
		content = deleteNodeFromJsonContent({
			jsonContent: content,
			deleteNode: (e) => !![Youtube.name, CustomImage.name, SmartLink.name].includes(e.type ?? ""),
		});

	useEffect(() => {
		if (divRef.current) {
			const currentHeight = divRef.current.offsetHeight;
			if (currentHeight > maxDivHeightinPixels) {
				setShowReadMore(true);
			} else {
				setShowReadMore(false);
			}
		}
	}, [content]);

	return (
		<button type="button" className="line-clamp-5 inline-block w-full text-left" onClick={onClick}>
			<h5 className="mb-3 font-semibold leading-tight tracking-tight text-gray-800 md:text-lg md:leading-normal">
				{post.title}
			</h5>
			<div
				// Using style to be able to use single variable for height
				style={{ maxHeight: `${maxDivHeightinPixels}px` }}
				className={cn(
					"relative flex  flex-col overflow-y-hidden",
					showReadMore &&
						"after:absolute after:bottom-0 after:left-0 after:right-0 after:h-14 after:bg-gradient-to-b after:from-transparent after:via-white/70 after:to-white after:ease-linear after:content-['']"
				)}
			>
				<JSONContentRenderer
					ref={divRef}
					className="flex flex-col gap-4"
					json={content || {}}
					extensions={renderingExtentions}
				/>
			</div>
			{showReadMore && <span className="text-sm font-medium text-emerald-600">Read more...</span>}
		</button>
	);
};

// TODO (zeekhuge): convert this to enum;
export type FeedPostUIType = "PREVIEW" | "MINIMAL" | "FULL";

const FeedPostUI = ({
	isEditable,
	isDeletable,
	post,
	classNames,
	uiType = "PREVIEW",
	...props
}: {
	post: ParsedFeedPost;
	classNames?: { card?: string };
	uiType?: FeedPostUIType;
	isEditable: boolean;
	isDeletable: boolean;
	onEditPost?: () => void;
	onShowFullPost?: () => void;
	onPostLike?: (like: boolean) => Promise<boolean>;
	isPinnable?: boolean;
	canPinToChannel?: boolean;
	canPinToCommunity?: boolean;
}) => {
	const { isPinnable, canPinToChannel, canPinToCommunity, onEditPost, onShowFullPost, onPostLike } =
		props;
	const isV2Post = post.version === "v2";

	const handleOpenEditPostModal = () => {
		if (post.version === "v2") {
			onEditPost?.();
		} else {
			toast.error("Unable to edit legacy posts");
		}
	};

	const contentSection = (
		<div className="mb-2 border-b pb-2">
			{isV2Post ? (
				<div>
					{(post.postType === "DISCUSSION" || uiType === "PREVIEW") && uiType !== "MINIMAL" && (
						<div className="mb-4">
							<MediaPanel post={post} />
						</div>
					)}

					{uiType === "FULL" ? <FullPostContent post={post} /> : <> </>}
					{uiType === "MINIMAL" ? (
						// TODO (zeekhuge): This Title only, should also be created into a minimal content-component;
						<button
							type="button"
							className="inline-block w-full text-left"
							onClick={() => onShowFullPost?.()}
						>
							<h5 className="mb-3 font-semibold leading-tight tracking-tight text-gray-800 md:text-lg md:leading-normal">
								{post.title}
							</h5>
						</button>
					) : (
						<> </>
					)}
					{uiType === "PREVIEW" ? (
						<PostContentPreview post={post} onClick={() => onShowFullPost?.()} />
					) : (
						<> </>
					)}
				</div>
			) : (
				<div>
					{post.postType === "DISCUSSION" && (
						<div className="mb-4">
							<MediaPanel post={post} />
						</div>
					)}
					<button
						type="button"
						className="inline-block w-full text-left"
						onClick={uiType === "FULL" ? undefined : () => onShowFullPost?.()}
					>
						<h5 className="mb-3 font-semibold leading-tight tracking-tight text-gray-800 md:text-lg md:leading-normal">
							{post.title}
						</h5>
						<div
							dangerouslySetInnerHTML={{ __html: post.legacyContent || "" }}
							className={cn("space-y-4 text-gray-600")}
						/>
					</button>
				</div>
			)}
		</div>
	);

	const actionButtons = (
		<div className="flex gap-2">
			{isEditable && (
				<button
					type="button"
					onClick={handleOpenEditPostModal}
					className="flex items-center justify-center gap-1 rounded-md border border-gray-300 p-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
				>
					<PencilIcon className="h-4 w-4" />
					<span className="sr-only">Edit</span>
				</button>
			)}

			{isDeletable && (
				<DeletePostAlertDialog
					postTitle={post.title}
					postSlug={post.slug}
					communitySlug={post.community.slug}
					channelSlug={post.channel.slug}
				/>
			)}

			{isPinnable && (
				<PinPostDropdown
					post={post}
					canPinToChannel={canPinToChannel}
					canPinToCommunity={canPinToCommunity}
				/>
			)}
		</div>
	);

	return (
		<>
			<div className={cn("w-full rounded-lg bg-white p-4 shadow", classNames?.card)}>
				<div className="mb-2 flex items-center justify-between gap-4">
					<div className="flex items-center justify-start gap-2">
						<Image
							src={
								post.channel.logo || post.community?.logo || "/images/default-placeholder-image.png"
							}
							alt={post.channel?.name || post.community.name || "Community"}
							width={480}
							height={480}
							className="h-5 w-5 rounded-md"
						/>

						<div className="flex items-center justify-start gap-px max-md:flex-wrap">
							<>
								<Link href={`/communities/${post.community?.slug}`}>
									<h4 className="text-sm font-semibold text-gray-600">{post.community?.name}</h4>
								</Link>

								<span className="hidden md:block">-</span>
							</>

							<Link href={`/communities/${post.community?.slug}/${post.channel?.slug}`}>
								<h4 className="text-sm font-semibold text-emerald-600">{post.channel?.name}</h4>
							</Link>
						</div>
					</div>

					<div className="flex gap-2">
						{(post.isPinnedToChannel || post.isPinnedToCommunity) && (
							<div className="flex items-center justify-center gap-1 text-sm font-semibold text-gray-500">
								<Pin className="h-4 w-4" />
							</div>
						)}
						<div className="flex items-center justify-center gap-1 text-sm font-semibold text-gray-500">
							<span>
								<EyeIcon className="h-4 w-4" />
							</span>
							<div className="flex gap-1">
								<span>{post.totalViews}</span>
								<span className="hidden sm:block">Views</span>
							</div>
						</div>
						{actionButtons}
					</div>
				</div>

				<div className="mb-2 flex items-center justify-between gap-4 border-b pb-2">
					<Link href={post.author.incognito ? "#" : `/${post.author.username}`}>
						<div className="flex items-center justify-start gap-2">
							<div className="h-9 w-9 md:h-auto md:w-auto">
								<ProfilePicture
									profilePictureUrl={post.author.image}
									alternateText={post.author.name}
									isVerified={post.author.incognito ? false : post.author.isVerified}
									isFounder={post.author.incognito ? false : post.author.isFounder}
									className={{
										container: "p-px",
										image: "h-7 w-7 rounded-full object-cover md:h-8 md:w-8",
										badgeImage: "-bottom-3 h-6 w-6 md:-bottom-4 md:h-8 md:w-8",
									}}
								/>
							</div>

							<div className="flex flex-col">
								<div>
									<div className="flex items-center justify-start gap-2">
										<span className="font-medium capitalize text-gray-600">{post.author.name}</span>

										{post.author.incognito && (
											<span title="Anonymous VIP ID">
												<IncognitoVIPIcon className="h-4 w-4" />
											</span>
										)}
									</div>

									<span className="text-xs text-gray-600">
										{post.author.jobTitle}
										{post.author.company ? `, ${post.author.company}` : ""}
									</span>
								</div>
							</div>
						</div>
					</Link>

					<div className="flex items-center justify-start gap-1">
						<ClockIcon className="h-4 w-4 text-gray-600" />

						<span className="hidden text-sm text-gray-600 md:block">
							{formatDistanceStrict(new Date(post.createdAt), new Date())}
						</span>
					</div>
				</div>

				{contentSection}

				<div className="mb-4 flex flex-wrap items-center justify-between gap-4 md:flex-nowrap">
					<div className="flex items-center justify-start gap-1">
						{post.topInteractedUsers.likes.length > 0 ? (
							<div className="flex items-center -space-x-2">
								{post.topInteractedUsers.likes.map((user) => (
									<Link key={user.id} href={`/user/${user.username}`}>
										<ProfilePicture
											profilePictureUrl={user.image}
											alternateText={user.name}
											className={{
												container: "bg-white p-px",
												image: "h-5 w-5 rounded-full md:h-8 md:w-8",
												badgeImage: "-bottom-3 h-6 w-6",
											}}
											isVerified={user.incognito ? false : user.isVerified}
											isFounder={user.incognito ? false : user.isFounder}
										/>
									</Link>
								))}

								{post.topInteractedUsers.comments.map((user) => (
									<Link key={user.id} href={`/user/${user.username}`}>
										<ProfilePicture
											profilePictureUrl={user.image}
											alternateText={user.name}
											className={{
												container: "bg-white p-px",
												image: "h-5 w-5 rounded-full md:h-8 md:w-8",
												badgeImage: "-bottom-3 h-6 w-6",
											}}
											isVerified={user.incognito ? false : user.isVerified}
											isFounder={user.incognito ? false : user.isFounder}
										/>
									</Link>
								))}
							</div>
						) : (
							<div className="flex items-center -space-x-4">
								<Image
									src="/images/person-1.jpg"
									alt="person image"
									width={100}
									height={100}
									className="h-10 w-10 rounded-full border-2 border-white object-cover"
								/>
								<Image
									src="/images/person-2.jpg"
									alt="person image"
									width={100}
									height={100}
									className="h-10 w-10 rounded-full border-2 border-white object-cover"
								/>
								<Image
									src="/images/person-3.jpg"
									alt="person image"
									width={100}
									height={100}
									className="h-10 w-10 rounded-full border-2 border-white object-cover"
								/>
							</div>
						)}
					</div>

					<div className="flex items-center justify-start gap-2 text-sm font-semibold text-gray-600">
						<span>{post.totalReactions} Likes</span>

						<span>{post.totalComments} Comments</span>
					</div>
				</div>

				<div className="flex items-center justify-between gap-2">
					<FeedPostLikeButton isLiked={post.isLikedByUser} onClick={onPostLike} />

					{uiType === "PREVIEW" && (
						<button
							type="button"
							onClick={() => onShowFullPost?.()}
							className="flex w-full items-center justify-center gap-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600"
						>
							<span>
								<MessageCircleIcon className="h-4 w-4" />
							</span>
							<span>Comment</span>
						</button>
					)}

					<FeedPostShareButton post={post} />
				</div>
			</div>
		</>
	);
};

export default FeedPostUI;

const PinPostDropdown = ({
	post,
	canPinToChannel,
	canPinToCommunity,
}: {
	post: ParsedFeedPost;
	canPinToChannel?: boolean;
	canPinToCommunity?: boolean;
}) => {
	const queryClient = useQueryClient();
	const { channelInfo } = useCommunityContext();

	const pinPostMutation = useMutation({
		mutationFn: async (type: "CHANNEL" | "COMMUNITY") => {
			const response = await fetch(`${POSTS_API}/${post.slug}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ type }),
			});
			if (!response.ok) {
				throw new Error("Failed to pin post");
			}
			return response.json();
		},
		onSuccess: (data) => {
			toast.success(data.message);
			queryClient.invalidateQueries(["post", post.slug]);
			queryClient.invalidateQueries(
				channelInfo
					? ["posts", post.community.slug, channelInfo.slug]
					: ["posts", post.community.slug]
			);
		},
		onError: (error: Error) => {
			toast.error(error.message);
		},
	});

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button className="flex items-center justify-center gap-1 rounded-md border border-gray-300 p-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50">
					<MoreHorizontal className="h-4 w-4" />
					<span className="sr-only">More options</span>
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				{canPinToChannel && (
					<DropdownMenuItem
						className="cursor-pointer"
						onClick={() => pinPostMutation.mutate("CHANNEL")}
					>
						{post.isPinnedToChannel ? "Unpin from Channel" : "Pin to Channel"}
					</DropdownMenuItem>
				)}
				{canPinToCommunity && (
					<DropdownMenuItem
						className="cursor-pointer"
						onClick={() => pinPostMutation.mutate("COMMUNITY")}
					>
						{post.isPinnedToCommunity ? "Unpin from Community" : "Pin to Community"}
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
